import { all, takeLatest } from 'redux-saga/effects';

import { fetchAssetDetails } from '@rikstv/play-common/src/forces/assetDetails/api';
import { commonSignUp, commonUpgrade } from '@rikstv/play-common/src/forces/loadUserInfo/actions';
import history from '@rikstv/play-common/src/router/history';
import { shopperFunnel } from '@rikstv/play-common/src/utils/analytics/posthogProvider';
import { captureExceptionInSentry } from '@rikstv/play-common/src/utils/errorTracker/tracking';
import { getLogger } from '@rikstv/play-common/src/utils/logger/logger';
import { AwaitedReturnType } from '@rikstv/play-common/src/utils/types/typeUtils';
import { sharedStrimUrls } from '@rikstv/shared-strim-paths';

import commonRouterPaths, { routerPaths } from '../../../common/router/router.path';
import { getNavigationUrlAndRecommendedProducts } from '../../packages/utils/getRecommendedProduct';
import { persistShopperAssetOnSignup } from '../../packages/utils/persistShopperAssetOnSignup';

const logger = getLogger('[signUpAndUpgradeSagas]');

function* startSignupSaga(action: ReturnType<typeof commonSignUp>) {
  try {
    if (action.payload.fromAssetId) {
      shopperFunnel.captureStart(action.payload.fromAssetId, 'signup');
      const assetDetails: AwaitedReturnType<typeof fetchAssetDetails> = yield fetchAssetDetails(
        action.payload.fromAssetId,
        null
      );
      const { recommendedProducts }: AwaitedReturnType<typeof getNavigationUrlAndRecommendedProducts> =
        yield getNavigationUrlAndRecommendedProducts({ assetDetails });
      shopperFunnel.captureRecommendation(action.payload.fromAssetId, 'signup', recommendedProducts);

      if (recommendedProducts) {
        persistShopperAssetOnSignup(assetDetails, recommendedProducts.packageId);
      }

      if (recommendedProducts?.products && recommendedProducts.products.length > 0) {
        const url = commonRouterPaths.signUpSelectPackage(recommendedProducts.products.map((id: string) => ({ id })));
        logger.info(`Navigating to ${url} with recommended products for asset ${action.payload.fromAssetId}`);
        history.push(url);
      } else {
        logger.info('No recommended products found, navigating to signup page');
        history.push(sharedStrimUrls.signup);
      }
    } else {
      logger.info('No assetId provided for signup, navigating to default signup page');
      history.push(sharedStrimUrls.signup);
    }
  } catch (err) {
    captureExceptionInSentry(err, { fingerprint: ['start-signup-error'], extra: { action } });
  }
}

function* handleUpgradeSaga({ payload }: ReturnType<typeof commonUpgrade>) {
  try {
    if (payload.fromAssetId) {
      shopperFunnel.captureStart(payload.fromAssetId, 'upgrade');
      const assetDetails: AwaitedReturnType<typeof fetchAssetDetails> = yield fetchAssetDetails(
        payload.fromAssetId,
        null
      );
      const { recommendedProducts }: AwaitedReturnType<typeof getNavigationUrlAndRecommendedProducts> =
        yield getNavigationUrlAndRecommendedProducts({ assetDetails });
      shopperFunnel.captureRecommendation(payload.fromAssetId, 'upgrade', recommendedProducts);

      if (recommendedProducts && recommendedProducts.products.length > 0) {
        // Use new-productIds to merge with existing subscription and resolve conflicts
        const newProductIds = recommendedProducts.products
          .map((id: string) => `new-productIds=${encodeURIComponent(id)}`)
          .join('&');
        const url = `${routerPaths.accountSubscriptionPage}?${newProductIds}`;
        logger.info(`Navigating to ${url} with recommended products for asset ${payload.fromAssetId}`);
        history.push(url);
      } else {
        logger.info('No recommended products found, navigating to subscription page');
        history.push(routerPaths.accountSubscriptionPage);
      }
    } else {
      logger.info('No assetId provided for upgrade, navigating to default upgrade page');
      history.push(routerPaths.accountSubscriptionPage);
    }
  } catch (err) {
    captureExceptionInSentry(err, { fingerprint: ['handle-upgrade-error'], extra: { payload } });
  }
}

export function* signUpAndUpgradeSagas() {
  yield all([takeLatest(commonSignUp.type, startSignupSaga), takeLatest(commonUpgrade.type, handleUpgradeSaga)]);
}
